<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="pt-4 pb-15">
    <div class="d-flex justify-end mb-2 px-4">
      <v-btn
        color="secondary"
        :ripple="false"
        :to="{ name: 'admin-report-settings' }"
        class="mr-4"
      >
        設定
      </v-btn>
      <v-btn
        color="primary"
        :ripple="false"
        @click="goToClosing"
      >
        <v-icon left>
          {{ icons.mdiClockEnd }}
        </v-icon>
        クロージング
      </v-btn>
    </div>

    <div class="mb-4 px-4">
      <v-menu
        transition="scale-transition"
        min-width="auto"
      >
        <template #activator="{ on, attrs }">
          <v-text-field
            :value="displayDate"
            :prepend-icon="icons.mdiCalendar"
            readonly
            class="text-base"
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker
          v-model="date"
          locale="ja"
          :day-format="d => new Date(d).getDate()"
          color="primary"
          no-title
        />
      </v-menu>
    </div>

    <!-- 集計の未保存・不整合（データ整合性診断） -->
    <div class="mb-6 px-4">
      <v-skeleton-loader
        v-if="diagnosticsLoading"
        type="list-item-two-line"
      />
      <template v-else>
        <unsaved-summary-banner
          :unsaved-day-count="diagnosticRows.length"
          :expanded="unsavedListExpanded"
          :error="diagnosticsError"
          :from="diagnosticRange.from"
          :to="diagnosticRange.to"
          @toggle="unsavedListExpanded = !unsavedListExpanded"
        />
        <v-expand-transition>
          <unsaved-list
            v-show="unsavedListExpanded && diagnosticRows.length > 0"
            :rows="diagnosticRows"
            :today="today"
            @go-to-closing="goToClosingForDate"
          />
        </v-expand-transition>
      </template>
    </div>

    <v-card-text class="pt-0 px-4">
      <v-row class="my-2">
        <v-col cols="4">
          <daily-weather
            :key="`daily-weather-${date}`"
            :date="date"
            :readonly="true"
          />
        </v-col>
        <v-col cols="8">
          <daily-club-event
            :key="`daily-club-event-${date}`"
            :date="date"
            :readonly="true"
          />
        </v-col>
      </v-row>
      <daily-report-closer
        :key="`daily-report-closer-${date}`"
        :date="date"
        :readonly="true"
      />
    </v-card-text>

    <div class="mt-8" />

    <v-skeleton-loader
      v-if="loading"
      type="table"
    />

    <template v-else-if="error">
      <v-alert type="error">
        {{ error }}
      </v-alert>
    </template>

    <template v-else>
      <!-- 収支 -->
      <v-card class="mb-8 daily-report-contents-wrapper">
        <v-card-title>
          収支
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="sections.length === 0"
            type="info"
          >
            セクションが設定されていません
          </v-alert>

          <v-row>
            <v-col
              v-for="section in sections"
              :key="`section-${section.id}`"
              cols="12"
              md="6"
            >
              <app-card-actions
                class="daily-report-contents-wrapper mb-6"
                action-collapse
              >
                <template #title>
                  {{ section.label }}
                </template>
                <v-card-text>
                  <v-row>
                    <v-col
                      v-for="metric in section.metrics"
                      :key="`metric-${metric.id}`"
                      cols="12"
                      class="pb-4"
                    >
                      <v-card outlined>
                        <v-card-text class="pt-2 px-2 px-md-8 pb-0">
                          <v-list subheader>
                            <v-list-item class="pa-0">
                              <v-list-item-content>
                                <div class="d-flex align-center justify-space-between">
                                  <div class="d-flex align-center" style="opacity: 0.75">
                                    <div
                                      class="d-flex align-center justify-center rounded mr-3"
                                      :style="{
                                        width: '44px',
                                        height: '44px',
                                        background: metric.icon_color ? `${metric.icon_color}1A` : 'rgba(158,158,158,0.1)',
                                      }"
                                    >
                                      <v-icon
                                        size="24"
                                        :color="metric.icon_color || '#9E9E9E'"
                                      >
                                        {{ resolveIcon(metric.icon_key) }}
                                      </v-icon>
                                    </div>
                                    <span
                                      class="text-base"
                                      :style="metric.icon_color ? `color: ${metric.icon_color}` : 'color: #9E9E9E'"
                                    >
                                      {{ metric.name }}
                                    </span>
                                  </div>
                                  <span
                                    class="font-weight-semibold text-xl"
                                    :class="metric.total < 0 ? 'error--text' : ''"
                                  >
                                    {{ formatAmount(metric.total) }}
                                  </span>
                                </div>
                              </v-list-item-content>
                            </v-list-item>

                            <v-list-item
                              v-for="item in metric.items"
                              :key="`item-${item.id}`"
                              class="px-0"
                              style="min-height: 28px;"
                            >
                              <v-list-item-content class="py-0">
                                <div
                                  class="d-flex align-center justify-space-between"
                                  style="padding-left: 20px; opacity: 0.65; font-size: 14px;"
                                >
                                  <span class="d-flex align-center">
                                    {{ item.label }}
                                    <v-chip
                                      v-if="item.has_user_breakdowns || item.has_expense_breakdown || item.has_benefit_breakdown"
                                      x-small
                                      color="primary"
                                      class="ml-2"
                                      style="cursor: pointer;"
                                      @click.stop="openBreakdown(item)"
                                    >
                                      内訳
                                    </v-chip>
                                  </span>
                                  <span :class="item.display_amount != null && item.display_amount < 0 ? 'error--text' : ''">
                                    {{ item.display_amount != null ? formatAmount(item.display_amount) : '-' }}
                                  </span>
                                </div>
                              </v-list-item-content>
                            </v-list-item>
                          </v-list>
                        </v-card-text>
                      </v-card>
                    </v-col>
                  </v-row>
                </v-card-text>
              </app-card-actions>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- パフォーマンス -->
      <v-card class="mb-8 daily-report-contents-wrapper">
        <v-card-title>
          パフォーマンス
        </v-card-title>

        <v-lazy
          v-model="isPerformanceActive"
          :options="{ threshold: .5 }"
          min-height="300"
          min-width="100%"
          transition="fade-transition"
        >
          <v-card-text>
            <app-card-actions
              class="daily-report-contents-wrapper mb-6"
              action-collapse
            >
              <template #title>
                顧客
              </template>
              <v-card-text class="mb-4">
                <v-skeleton-loader
                  v-if="isLoadingPerformanceContent"
                  type="table"
                />
                <customer-counts
                  v-else
                  :date="date"
                  :sales-amount="salesAmount"
                  :customer-count="performance.customerCount"
                  :customer-group-count="performance.customerGroupCount"
                  :unguided-customer-count="performance.unguidedCustomerCount"
                  :operation-rate="performance.operationRate"
                  :hourly-customer-count-summaries="hourlyCustomerCountSummaries"
                  :table-counts-by-check-amount-range="tableCountsByCheckAmountRange"
                  :readonly="false"
                />
              </v-card-text>
              <v-card-text>
                <v-skeleton-loader
                  v-if="isLoadingPerformanceContent"
                  type="table"
                />
                <table-result-overviews
                  v-else
                  :items="tableResultOverviews"
                  :dynamic-headers="dynamicTableResultOverviewHeaders"
                  :sales-per-customer-tag="performance.salesPerCustomerTag"
                  :date="date"
                  :interval="0"
                />
              </v-card-text>
            </app-card-actions>

            <realtime-user-aggregations :date="date" />

            <eod-closing-user-aggregations :date="date" />
          </v-card-text>
        </v-lazy>
      </v-card>
    </template>

    <div class="mt-4 px-4">
      <download-daily-report-summary-btn
        :date="date"
        :sections="sections"
      />
    </div>

    <!-- 内訳モーダル -->
    <v-dialog
      v-model="breakdownDialogOpen"
      max-width="480"
      scrollable
    >
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ selectedBreakdownItem && selectedBreakdownItem.label }} の内訳</span>
          <v-btn icon @click="breakdownDialogOpen = false">
            <v-icon>{{ icons.mdiClose }}</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <v-skeleton-loader
            v-if="breakdownLoading"
            type="table-tbody"
          />
          <v-simple-table
            v-else
            dense
          >
            <tbody>
              <tr
                v-for="(b, index) in breakdowns"
                :key="isLabelBreakdown ? `lbl-${index}` : b.user_id"
              >
                <td>
                  <template v-if="isLabelBreakdown">
                    <div>{{ b.label }}</div>
                    <div class="mt-1">
                      <v-chip
                        v-if="b.cash_amount > 0"
                        x-small
                        color="primary"
                        class="mr-1"
                      >
                        現金 {{ formatAmount(b.cash_amount) }}
                      </v-chip>
                      <v-chip
                        v-if="b.non_cash_amount > 0"
                        x-small
                        color="secondary"
                      >
                        非現金 {{ formatAmount(b.non_cash_amount) }}
                      </v-chip>
                    </div>
                  </template>
                  <template v-else>
                    {{ b.user_name }}
                  </template>
                </td>
                <td
                  class="text-right"
                  :class="(isLabelBreakdown ? b.amount : b.display_amount) < 0 ? 'error--text' : ''"
                >
                  {{ formatAmount(isLabelBreakdown ? b.amount : b.display_amount) }}
                </td>
              </tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  ref,
  reactive,
  computed,
  inject,
  onMounted,
  watch,
} from '@vue/composition-api'
import {
  mdiCalendar,
  mdiClockEnd,
  mdiTrendingUp,
  mdiCurrencyJpy,
  mdiChartBar,
  mdiChartLine,
  mdiInformationOutline,
  mdiClose,
} from '@mdi/js'
import { useRouter } from '@core/utils'
import AppCardActions from '@core/components/app-card-actions/AppCardActions.vue'
import CustomerCounts from '@/views/components/daily_report/BusinessResult/CustomerCounts.vue'
import TableResultOverviews from '@/views/components/daily_report/BusinessResult/TableResultOverviews.vue'
import DailyWeather from '@/views/components/daily_report/Closing/DailyWeather.vue'
import DailyClubEvent from '@/views/components/daily_report/Closing/DailyClubEvent.vue'
import DailyReportCloser from '@/views/components/daily_report/Closing/DailyReportCloser.vue'
import DownloadDailyReportSummaryBtn from './DownloadDailyReportSummaryBtn.vue'
import EodClosingUserAggregations from './EodClosingUserAggregations.vue'
import RealtimeUserAggregations from './RealtimeUserAggregations.vue'
import UnsavedSummaryBanner from './components/UnsavedSummaryBanner.vue'
import UnsavedList from './components/UnsavedList.vue'
import DailyReportSummaryApi from '@/api/v2/eod-report/DailyReportSummary'
import FormulaItemBreakdownApi from '@/api/v2/eod-report/FormulaItemBreakdown'
import FormulaItemExpenseBreakdownApi from '@/api/v2/eod-report/FormulaItemExpenseBreakdown'
import FormulaItemBenefitBreakdownApi from '@/api/v2/eod-report/FormulaItemBenefitBreakdown'
import SalesApi from '@/api/admin/Sales'
import TableSummaryApi from '@/api/admin/TableSummary'
import FinanceApi from '@/api/admin/Finance'
import useCompInit from '@/views/composable/useCompInit'
import useDataIntegrityDiagnostics from '@/views/v2-temp/common/composables/useDataIntegrityDiagnostics'

export default {
  components: {
    AppCardActions,
    CustomerCounts,
    TableResultOverviews,
    DailyWeather,
    DailyClubEvent,
    DailyReportCloser,
    DownloadDailyReportSummaryBtn,
    EodClosingUserAggregations,
    RealtimeUserAggregations,
    UnsavedSummaryBanner,
    UnsavedList,
  },
  setup() {
    const { initWith } = useCompInit()
    const { route, router } = useRouter()
    const currentClubMeta = inject('currentClubMeta', { today: null })
    const today = currentClubMeta.today || new Date().toISOString().slice(0, 10)
    const date = ref(route.value.query.date || today)

    // 収支
    const sections = ref([])
    const loading = ref(false)
    const error = ref(null)

    // パフォーマンス
    const isPerformanceActive = ref(false)
    const isLoadingPerformanceContent = ref(false)
    let performanceLoaded = false
    const performance = reactive({
      customerCount: 0,
      customerGroupCount: 0,
      unguidedCustomerCount: 0,
      operationRate: 0,
      salesPerCustomerTag: {},
    })
    const hourlyCustomerCountSummaries = ref([])
    const tableCountsByCheckAmountRange = ref([])
    const tableResultOverviews = ref([])
    const dynamicTableResultOverviewHeaders = ref([])
    const incomeDetail = reactive({ cashAmount: 0, cardAmount: 0, balanceReceivableAmount: 0 })
    const salesAmount = computed(() => incomeDetail.cashAmount + incomeDetail.cardAmount + incomeDetail.balanceReceivableAmount)

    const displayDate = computed(() => (date.value ? date.value.replace(/-/g, '/') : ''))

    // 内訳モーダル
    const breakdownDialogOpen = ref(false)
    const breakdownLoading = ref(false)
    const selectedBreakdownItem = ref(null)
    const breakdowns = ref([])

    // 内訳が label ベース（経費・諸入費）かユーザーベース（has_user_breakdowns）か
    const isLabelBreakdown = computed(() => Boolean(
      selectedBreakdownItem.value
        && (selectedBreakdownItem.value.has_expense_breakdown
          || selectedBreakdownItem.value.has_benefit_breakdown),
    ))

    const openBreakdown = async item => {
      selectedBreakdownItem.value = item
      breakdownDialogOpen.value = true
      breakdownLoading.value = true
      try {
        let res
        if (item.has_expense_breakdown) {
          res = await FormulaItemExpenseBreakdownApi.getBreakdown(item.id, date.value)
        } else if (item.has_benefit_breakdown) {
          res = await FormulaItemBenefitBreakdownApi.getBreakdown(item.id, date.value)
        } else {
          res = await FormulaItemBreakdownApi.getBreakdown(item.id, date.value)
        }
        breakdowns.value = res.data.breakdowns
      } finally {
        breakdownLoading.value = false
      }
    }

    const formatAmount = amount => {
      if (amount == null) return '-'

      return `¥${Math.abs(Number(amount)).toLocaleString()}`
    }

    const ICON_MAP = {
      mdiTrendingUp,
      mdiCurrencyJpy,
      mdiChartBar,
    }

    const resolveIcon = key => ICON_MAP[key] || mdiChartLine

    // 新クロージングページへの遷移（日報βからの導線）
    const goToClosing = () => {
      router.push({ name: 'eod-closing-v2', query: { date: date.value } })
    }

    // 未保存リストの行から、該当営業日の新クロージングページへ遷移
    const goToClosingForDate = businessDate => {
      router.push({ name: 'eod-closing-v2', query: { date: businessDate } })
    }

    // データ整合性診断（未保存サマリーバナー＋未保存リスト）
    const {
      loading: diagnosticsLoading,
      error: diagnosticsError,
      rows: diagnosticRows,
      fetchDiagnostics,
      diagnosticDateRange,
    } = useDataIntegrityDiagnostics()
    const unsavedListExpanded = ref(false)
    // 診断の対象期間（前月初日〜今日）。サマリーバナーに表示する。
    const diagnosticRange = diagnosticDateRange(today)

    const loadDiagnostics = () => {
      fetchDiagnostics(diagnosticRange)
    }

    // 収支データ取得
    const fetchSummary = async () => {
      if (!date.value) return
      loading.value = true
      error.value = null
      try {
        const res = await DailyReportSummaryApi.getSummary(date.value)
        sections.value = res.data.sections || []
      } catch (e) {
        error.value = 'データの取得に失敗しました'
      } finally {
        loading.value = false
      }
    }

    // パフォーマンスデータ取得
    const getPerformance = async () => {
      const res = await SalesApi.getPerformance({ date: date.value, interval: 0, endDate: '' })
      if (res?.status === 200) {
        performance.customerCount = Number(res.data.performance.customerCount)
        performance.customerGroupCount = Number(res.data.performance.customerGroupCount)
        performance.unguidedCustomerCount = Number(res.data.performance.unguidedCustomerCount)
        performance.operationRate = Number(res.data.performance.operationRate)
        performance.salesPerCustomerTag = res.data.performance.salesPerCustomerTag
      }
    }

    const getHourlyCustomerCounts = async () => {
      const res = await TableSummaryApi.getHourlyCustomerCounts({ date: date.value, interval: 0, endDate: '' })
      if (res?.status === 200) hourlyCustomerCountSummaries.value = res.data.hourlyCustomerCountSummaries
    }

    const getTableCountsByCheckAmountRange = async () => {
      const res = await TableSummaryApi.getTableCountsByCheckAmountRange({ date: date.value, interval: 0, endDate: '' })
      if (res?.status === 200) tableCountsByCheckAmountRange.value = res.data.tableCountsByCheckAmountRange
    }

    const getTableResultOverviews = async () => {
      const res = await TableSummaryApi.getOverviews({ date: date.value, interval: 0, endDate: '' })
      if (res?.status === 200) {
        tableResultOverviews.value = res.data.overviews
        dynamicTableResultOverviewHeaders.value = res.data.dynamicHeaders
      }
    }

    const getIncomeDetail = async () => {
      const res = await FinanceApi.getIncomeDetail({ date: date.value, interval: 0, endDate: '' })
      if (res?.status === 200) {
        incomeDetail.cashAmount = Number(res.data.cashAmount)
        incomeDetail.cardAmount = Number(res.data.cardAmount)
        incomeDetail.balanceReceivableAmount = Number(res.data.balanceReceivableAmount)
      }
    }

    const loadPerformance = () => {
      performanceLoaded = true
      initWith(
        [
          getPerformance(),
          getHourlyCustomerCounts(),
          getTableCountsByCheckAmountRange(),
          getTableResultOverviews(),
          getIncomeDetail(),
        ],
        isLoadingPerformanceContent,
      )
    }

    // v-lazy でビューポートに入ったら初回ロード
    watch(() => isPerformanceActive.value, (newOn, prevOn) => {
      if (!prevOn && newOn) loadPerformance()
    })

    // 日付変更時: URLを更新し、収支と（すでにロード済みなら）パフォーマンスも再取得
    watch(() => date.value, newDate => {
      router.replace({ query: { ...route.value.query, date: newDate } })
      fetchSummary()
      if (performanceLoaded) loadPerformance()
    })

    onMounted(() => {
      fetchSummary()
      loadDiagnostics()
    })

    return {
      date,
      today,
      displayDate,
      sections,
      loading,
      error,
      diagnosticsLoading,
      diagnosticsError,
      diagnosticRows,
      diagnosticRange,
      unsavedListExpanded,
      goToClosingForDate,
      isPerformanceActive,
      isLoadingPerformanceContent,
      performance,
      hourlyCustomerCountSummaries,
      tableCountsByCheckAmountRange,
      tableResultOverviews,
      dynamicTableResultOverviewHeaders,
      salesAmount,
      formatAmount,
      resolveIcon,
      goToClosing,
      breakdownDialogOpen,
      breakdownLoading,
      selectedBreakdownItem,
      breakdowns,
      isLabelBreakdown,
      openBreakdown,
      icons: {
        mdiCalendar,
        mdiClockEnd,
        mdiInformationOutline,
        mdiClose,
      },
    }
  },
}
</script>

<style lang="scss" scoped>
.daily-report-contents-wrapper {
  background: rgba(0, 0, 0, .06);
  border-radius: 10px;
  backdrop-filter: blur(10px);
}
</style>
